import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";

import { db } from "../../../firebase/firebase";

const URGENCY_TO_CHIP_COLOR = {
  immediate: "error",
  high: "error",
  medium: "warning",
  low: "default",
};

const URGENCY_PRIORITY = {
  immediate: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const SEVERITY_TO_CHIP_COLOR = {
  high: "error",
  medium: "warning",
  low: "default",
};

const normalizeTimestamp = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate().toString();
  if (value instanceof Date) return value.toString();
  return value;
};

const toDateObject = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const filterDeliveredDigest = (messages = []) =>
  messages.filter(
    (msg) =>
      msg?.coordinatorDelivery?.channel === "digest" &&
      Array.isArray(msg.digest_items) &&
      msg.digest_items.length > 0
  );

export const useCoordinatorPlan = ({
  uid,
  messages,
  dismissedProposalIds = [],
}) => {
  const [plan, setPlan] = useState(null);
  const [rawQueuedProposals, setRawQueuedProposals] = useState([]);
  const dismissedSet = useMemo(
    () => new Set(dismissedProposalIds),
    [dismissedProposalIds]
  );

  useEffect(() => {
    if (!uid) return undefined;
    const planRef = doc(db, "users", uid, "coordinatorState", "currentPlan");
    const unsubscribe = onSnapshot(planRef, (snapshot) => {
      if (!snapshot.exists()) {
        setPlan(null);
        return;
      }
      const data = snapshot.data();
      setPlan({
        ...data,
        generatedAt: normalizeTimestamp(data.generatedAt),
        digest: {
          ...(data.digest || {}),
          scheduledFor: normalizeTimestamp(data.digest?.scheduledFor),
          lastDeliveredAt: normalizeTimestamp(data.digest?.lastDeliveredAt),
        },
      });
    });
    return unsubscribe;
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    const proposalsRef = collection(db, "users", uid, "agentProposals");
    const queuedQuery = query(proposalsRef, where("status", "==", "queued"));
    const unsubscribe = onSnapshot(queuedQuery, (snapshot) => {
      if (snapshot.empty) {
        setRawQueuedProposals([]);
        return;
      }

      const items = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data() || {};
          const structured = data.structuredOutreach || data.structured_outreach || null;
          const createdDate = toDateObject(data.createdAt);
          const deliverAfterDate = toDateObject(data.deliverAfter);
          return {
            id: docSnap.id,
            agentId: data.agentId,
            summary:
              data.summary || structured?.summary || data.message || "Pending outreach",
            urgency: structured?.urgency || data.urgency || "medium",
            loss_if_ignored:
              structured?.loss_if_ignored || data.loss_if_ignored || "",
            recommended_next_step:
              structured?.recommended_next_step || data.recommended_next_step || "",
            effort_minutes:
              structured?.effort_minutes ?? data.effort_minutes ?? null,
            createdAt: normalizeTimestamp(createdDate),
            createdAtValue: createdDate ? createdDate.getTime() : 0,
            deliverAfter: normalizeTimestamp(deliverAfterDate),
            deliverAfterValue: deliverAfterDate ? deliverAfterDate.getTime() : null,
            structuredOutreach: structured,
            supportingEvidence:
              data.supportingEvidence || data.supporting_evidence || null,
          };
        })
        .sort((a, b) => {
          const urgencyDiff =
            (URGENCY_PRIORITY[a.urgency] ?? URGENCY_PRIORITY.medium) -
            (URGENCY_PRIORITY[b.urgency] ?? URGENCY_PRIORITY.medium);
          if (urgencyDiff !== 0) return urgencyDiff;
          const readinessA =
            a.deliverAfterValue ?? (a.createdAtValue ?? 0);
          const readinessB =
            b.deliverAfterValue ?? (b.createdAtValue ?? 0);
          if (readinessA !== readinessB) return readinessA - readinessB;
          return (a.createdAtValue || 0) - (b.createdAtValue || 0);
        });

      setRawQueuedProposals(items);
    });

    return unsubscribe;
  }, [uid]);

  const queuedProposals = useMemo(
    () => rawQueuedProposals.filter((item) => !dismissedSet.has(item.id)),
    [rawQueuedProposals, dismissedSet]
  );

  const coordinatorDigestMessages = useMemo(
    () => filterDeliveredDigest(messages),
    [messages]
  );

  const latestDigestMessage =
    coordinatorDigestMessages.length > 0
      ? coordinatorDigestMessages[0]
      : null;

  const bannerFocus = useMemo(() => {
    if (!plan?.focus || !Array.isArray(plan.focus)) {
      return null;
    }
    return plan.focus.find(
      (item) =>
        (item?.deliveryChannel === "banner" ||
          item?.deliveryChannel === "agent") &&
        (item?.status === "delivered" || !item?.status)
    );
  }, [plan]);

  const digestPreview = useMemo(() => {
    if (!plan?.digest?.items?.length) return null;
    const items = plan.digest.items.filter(
      (item) => !dismissedSet.has(item.proposalId)
    );
    if (latestDigestMessage) {
      const deliveredIds = new Set(
        latestDigestMessage.digest_items.map((item) => item.proposalId)
      );
      const remaining = items.filter(
        (item) => !deliveredIds.has(item.proposalId)
      );
      if (remaining.length === 0) {
        return null;
      }
      return { ...plan.digest, items: remaining };
    }
    return plan.digest;
  }, [plan, latestDigestMessage, dismissedSet]);

  const capacityCaption = useMemo(() => {
    if (!plan?.capacity?.label) return null;
    const label = plan.capacity.label;
    const detail = plan.capacity.detail;
    const labelMap = {
      engaged: "Engaged - coordinator will pace nudges cautiously.",
      steady: "Steady - open to focused nudges.",
      stretched: "Stretched - coordinator is limiting outreach to what matters most.",
      overloaded: "Overloaded - only critical escalations will surface right now.",
    };
    return `${labelMap[label] || `Coordinator status: ${label}`}${
      detail ? ` ${detail}` : ""
    }`;
  }, [plan]);

  const digestHistorySummary = useMemo(() => {
    if (!latestDigestMessage) {
      return {
        lastDeliveredAt: plan?.digest?.lastDeliveredAt || null,
        entries: [],
      };
    }

    return {
      lastDeliveredAt:
        latestDigestMessage.coordinatorDelivery?.createdAt ||
        plan?.digest?.lastDeliveredAt ||
        null,
      entries: latestDigestMessage.digest_items.map((item) => ({
        ...item,
        urgency: item.urgency || "medium",
        urgencyChip:
          URGENCY_TO_CHIP_COLOR[item.urgency] || URGENCY_TO_CHIP_COLOR.medium,
      })),
    };
  }, [latestDigestMessage, plan]);

  const getStructuredOutreachMeta = useMemo(
    () => (structuredOutreach = {}) => ({
      urgencyChip:
        URGENCY_TO_CHIP_COLOR[structuredOutreach?.urgency] ||
        URGENCY_TO_CHIP_COLOR.medium,
      typeChip: structuredOutreach?.type
        ? structuredOutreach.type.toLowerCase()
        : null,
    }),
    []
  );

  const getSignalSeverityChip = useMemo(
    () => (signal = {}) =>
      SEVERITY_TO_CHIP_COLOR[signal.severity] ||
      SEVERITY_TO_CHIP_COLOR.medium,
    []
  );

  return {
    plan,
    bannerFocus,
    digestPreview,
    capacityCaption,
    digestHistorySummary,
    getStructuredOutreachMeta,
    getSignalSeverityChip,
    queuedProposals,
  };
};
